import SyosEvents from './utils/SyosEvents';
import SyosMaximumSeatsReachedModal from './modals/SyosMaximumSeatsReachedModal';
import SyosRemoveSeatConfirmationModal from './modals/SyosRemoveSeatConfirmationModal';
import { testIsFakeMozMouseEvent, EventHandler } from '../utils/dom-events';
import EventHandlerList from '../utils/EventHandlerList';
import GestureHelper from './utils/GestureHelper';
import SeatAriaLabelRenderer from './templates/SeatAriaLabelRenderer';
import type SeatIconState from './seatIcons/SeatIconState';
import type SyosController from './SyosController';
import { ExtendedPriceTypeData, SyosSeatData } from './api/types';
import type SeatClientRectProvider from './utils/SeatClientRectProvider';
import GraphicType from './api/GraphicType';
import SeatIcon from './seatIcons/SeatIcon';
import SyosGrid from './grid/SyosGrid';

const Key = {
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  HOME: 36,
  END: 35,
};

function applySeatElementStyling(seat: SyosSeat, className: string | null, color: string | null, iconState: SeatIconState, isAvailable: boolean, isSelected: boolean) {
  const $el = seat.$el!;
  const $use = seat.$use!;

  if (className !== null) {
    if ($el.classList) {
      $el.classList.add(className);
    } else {
      const classList = $el.getAttribute('class')!.split(' ');

      if (!classList.includes(className)) {
        classList.push(className);
      }

      $el.setAttribute('class', classList.join(' '));
    }
  }

  if (color !== null) {
    $el.style.color = color;
  }

  if (isAvailable) {
    $el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
  }

  $el.classList.toggle('tn-syos-seat-map__seat--selected', isSelected);

  $use.setAttribute('xlink:href', `#${iconState.id}`);
}

class SyosSeat {
  static CSS_CLASS = 'tn-syos-seat-map__seat';

  static CSS_CLASS_UNAVAILABLE = 'tn-syos-seat-map__seat--unavailable';

  static GRID_UNIT_SIZE = 72;

  static GRID_MARGIN = 0;

  gestureHelper?: GestureHelper;

  mouseHandlers?: EventHandlerList;

  focusHandler?: EventHandler;

  blurHandler?: EventHandler;

  keyDownHandler?: EventHandler;

  $el?: SVGGElement;

  $use?: SVGUseElement;

  grid?: SyosGrid;

  static adjustValueForGrid(value: number) {
    return (SyosSeat.GRID_UNIT_SIZE * value) + (SyosSeat.GRID_MARGIN * value);
  }

  graphicType: GraphicType;

  icon: SeatIcon;

  selectedPriceType?: ExtendedPriceTypeData;

  constructor(private syosController: SyosController, private seatData: SyosSeatData, private clientRectProvider: SeatClientRectProvider) {
    this.graphicType = seatData.graphicType;

    this.icon = seatData.icon;
  }

  get zoneId() { return this.seatData.zone.id; }

  get id() { return this.seatData.id; }

  get number() { return this.seatData.seatNumber; }

  get row() { return this.seatData.row; }

  get statusDescription() { return this.seatData.statusDescription; }

  get description() { return this.seatData.description; }

  get zoneLabel() { return this.seatData.zoneLabel; }

  get status() { return this.seatData.status; }

  get type() { return this.seatData.type; }

  get sectionId() { return this.seatData.sectionId; }

  get gridRow() { return this.seatData.y; }

  get gridColumn() { return this.seatData.x; }

  get sectionDescription() { return this.seatData.sectionDescription; }

  get x() { return SyosSeat.adjustValueForGrid(this.seatData.x); }

  get y() { return SyosSeat.adjustValueForGrid(this.seatData.y); }

  get fillColor() { return this.seatData.fillColor; }

  get stateIcon() {
    if (this.getIsInTempCart()) {
      return this.icon.selected;
    }

    return this.icon.default;
  }

  get price() {
    return this.selectedPriceType!.price;
  }

  get feeDisclaimer() {
    return this.selectedPriceType!.feeDisclaimer;
  }

  get priceWithFee() {
    return this.selectedPriceType!.priceWithFee;
  }

  get displayPrice() {
    return this.selectedPriceType!.displayPrice;
  }

  get selectedPriceTypeId() {
    return this.selectedPriceType!.price_type;
  }

  get selectedPriceTypeDescription() {
    return this.selectedPriceType!.price_type_desc;
  }

  getClientRect() {
    return this.clientRectProvider.get(this);
  }

  setEl($el: SVGGElement) {
    this.gestureHelper = new GestureHelper($el, {
      onPress: this.onPress,
      onClick: this.onClick,
      makeKeyboardClickable: true,
      // debugId: `Seat ${this.id}`,
    }).start();

    this.mouseHandlers = new EventHandlerList([
      new EventHandler($el, 'mouseover', this.onMouseOver),
    ]).start();

    this.focusHandler = new EventHandler($el, 'focus', this.onFocus).start();
    this.blurHandler = new EventHandler($el, 'blur', this.onBlur).start();
    this.keyDownHandler = new EventHandler($el, 'keydown', this.onKeyDown).start();

    this.$el = $el;

    this.$use = $el.firstElementChild as SVGUseElement;

    this.updateAriaLabel();

    this.updateUiState();
  }

  setGrid(grid: SyosGrid) {
    this.grid = grid;
  }

  focus() {
    const $el = this.$el!;

    $el.setAttribute('focusable', 'true');

    if ($el.focus) {
      $el.focus();
      return;
    }

    try {
      // IE11 SVGElement does not have a focus method
      window.HTMLElement.prototype.focus.call($el);
    } catch (e) {
    }
  }

  onFocus = () => {
    window.requestAnimationFrame(() => {
      this.trigger(SyosEvents.SEAT_FOCUS);
    });
  };

  onBlur = () => {
    this.trigger(SyosEvents.SEAT_BLUR);
  };

  onKeyDown = (event: KeyboardEvent) => {
    const key = event.keyCode || event.charCode;

    if (event.ctrlKey && event.shiftKey) {
      return;
    }

    const grid = this.grid!;

    if (event.shiftKey) {
      switch (key) {
        case Key.HOME:
          event.preventDefault();
          grid.moveToFirstRow(this);
          break;
        case Key.END:
          event.preventDefault();
          grid.moveToLastRow(this);
          break;
        default:
          break;
      }

      return;
    }

    if (event.ctrlKey) {
      switch (key) {
        case Key.HOME:
          event.preventDefault();
          grid.moveToFirstSeat();
          break;
        case Key.END:
          event.preventDefault();
          grid.moveToLastSeat();
          break;
        default:
          break;
      }

      return;
    }

    switch (key) {
      case Key.UP:
        event.preventDefault();
        grid.moveUp(this);
        break;
      case Key.DOWN:
        event.preventDefault();
        grid.moveDown(this);
        break;
      case Key.LEFT:
        event.preventDefault();
        grid.moveLeft(this);
        break;
      case Key.RIGHT:
        event.preventDefault();
        grid.moveRight(this);
        break;
      case Key.HOME:
        event.preventDefault();
        grid.moveToStartOfRow(this);
        break;
      case Key.END:
        event.preventDefault();
        grid.moveToEndOfRow(this);
        break;
      default:
        break;
    }
  };

  onPress = () => {
    if (this.getIsAvailable()) {
      this.focus();
    }
  };

  onMouseOver = (event: MouseEvent) => {
    if (
      event.buttons
      || (event.touches && event.touches.length)
      || event.timeStamp === 0
      || testIsFakeMozMouseEvent(event)
    ) {
      return;
    }

    this.trigger(SyosEvents.SEAT_MOUSE_OVER);
  };

  trigger(syosEventName: string) {
    this.syosController.emit(syosEventName, { seat: this });
  }

  detach() {
    this.gestureHelper?.destroy();
    this.gestureHelper = undefined;

    this.mouseHandlers?.destroy();
    this.mouseHandlers = undefined;

    this.focusHandler?.destroy();
    this.focusHandler = undefined;

    this.blurHandler?.destroy();
    this.blurHandler = undefined;

    this.keyDownHandler?.destroy();
    this.keyDownHandler = undefined;

    this.grid = undefined;

    this.$el = undefined;
    this.$use = undefined;
  }

  onClick = async (event: MouseEvent) => {
    event.preventDefault();

    if (
      this.getIsAvailable() === false
      || this.getPriceTypes().length === 0
    ) {
      return;
    }

    const isRemovingSeat = this.getIsInTempCart();

    if (isRemovingSeat) {
      const isConfirmed = await SyosRemoveSeatConfirmationModal.confirm(
        this.syosController,
        this,
        this.$el,
      );

      if (isConfirmed) {
        this.removeFromCart();
      }
    } else {
      if (this.syosController.getCartContainer()!.maximumSeatCountReached()) {
        SyosMaximumSeatsReachedModal.open(this.syosController);
      } else {
        this.trigger(SyosEvents.SEAT_CLICK);
      }
    }
  };

  removeFromCart() {
    this.selectedPriceType = undefined;
    if (this.$el) {
      this.updateAriaLabel();
      this.updateUiState();
    }
    this.trigger(SyosEvents.SEAT_REMOVED);
  }

  selectPriceType(priceTypeId: number) {
    const priceType = this.syosController.getDataService()!.getPriceType(this.zoneId, priceTypeId)!;
    this.selectedPriceType = priceType;
    this.updateAriaLabel();
    this.updateUiState();
    this.trigger(SyosEvents.SEAT_SELECTED);
  }

  getPriceTypes() {
    return this.syosController.getDataService()!.getZonePriceTypes(this.zoneId);
  }

  getZone() {
    return this.seatData.zone;
  }

  updateAriaLabel() {
    const value = this.selectedPriceType
      ? SeatAriaLabelRenderer.getSelectedSeatLabel(this)
      : SeatAriaLabelRenderer.getSeatLabel(this);

    this.$el?.setAttribute('aria-label', value);
  }

  getIsAvailable() {
    return this.status === 0;
  }

  getIsInTempCart() {
    return !!this.selectedPriceType;
  }

  updateUiState() {
    let color = null;
    let className = null;
    const isAvailable = this.getIsAvailable();
    const isInTempCart = this.getIsInTempCart();
    const icon = isInTempCart ? this.icon.selected : this.icon.default;

    if (isAvailable === false) {
      className = SyosSeat.CSS_CLASS_UNAVAILABLE;
    } else {
      color = this.fillColor;
    }

    applySeatElementStyling(this, className, color, icon, isAvailable, isInTempCart);
  }
}

export default SyosSeat;
